/* ORION: the homepage after its first banner (sections orion-home-*, styles in assets/orion-home.css). Each section
   loads this file, so it runs once for all of them:
   - parts rise in as they come into view, and a block's loops rest while it is off screen;
   - the series: the open panel plays its film and they take turns (the mouse opens the one it is over); on narrow
     screens every card is open and the film of the one in view plays;
   - the numbers count up when seen;
   - the Sleep Match: the guide of /pages/find-your-mattress as it is (its questions, mattresses, ranking and results),
     in the homepage's look, moving smoothly from question to question;
   - the voices move on by themselves, one card at a time, and with the arrows.
   By the owner's rule all of it plays also when the system asks for reduced motion. */
(() => {
  if (window.orionHome) return;
  window.orionHome = true;
  const watch = (callback, options) => new IntersectionObserver(callback, options);

  const reveal = watch((entries) => entries.forEach((entry) => {
    if (!entry.isIntersecting) return;
    entry.target.classList.add('is-in');
    reveal.unobserve(entry.target);
  }), { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
  const rest = watch((entries) => entries.forEach((entry) => entry.target.classList.toggle('is-resting', !entry.isIntersecting)), { rootMargin: '160px 0px' });

  // The sections after the first banner wait, unrendered, until the first screen is painted and then until they come
  // near (sections/orion-home-stage.liquid: content-visibility); each one is let in for good about a screen before it
  // arrives.
  {
    const stage = document.querySelector('#MainContent > .shopify-section orion-home-stage')?.closest('.shopify-section');
    const startNear = () => {
      const near = watch((entries) => entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-near');
        near.unobserve(entry.target);
      }), { rootMargin: '100% 0px' });
      for (let next = stage.nextElementSibling; next; next = next.nextElementSibling) {
        if (next.classList.contains('shopify-section')) near.observe(next);
      }
    };
    if (stage) {
      if (document.documentElement.classList.contains('oh-painted')) startNear();
      else document.addEventListener('orion:painted', startNear, { once: true });
    }
  }

  // ------------------------------------------------------------------------------------------------ The series
  const CYCLE_MS = 6500;
  function series(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const cards = [...root.querySelectorAll('[data-oh-card]')];
    if (!cards.length) return;
    // The posters come once the page has loaded (or at the first scroll), so the first banner's picture has the line
    // to itself (owner, 2026-10-05: speed); the markup carries them as data-src / data-srcset.
    const posters = [...root.querySelectorAll('img[data-srcset], img[data-src]')];
    if (posters.length) {
      let fetched = false;
      const fetchPosters = () => {
        if (fetched) return;
        fetched = true;
        window.removeEventListener('scroll', fetchPosters);
        posters.forEach((img) => { if (img.dataset.srcset) img.srcset = img.dataset.srcset; if (img.dataset.src) img.src = img.dataset.src; });
      };
      window.addEventListener('scroll', fetchPosters, { passive: true });
      const afterLoad = () => (window.requestIdleCallback ? requestIdleCallback(fetchPosters, { timeout: 1500 }) : setTimeout(fetchPosters, 300));
      if (document.readyState === 'complete') afterLoad(); else window.addEventListener('load', afterLoad, { once: true });
    }
    const wide = window.matchMedia('(min-width: 990px)');
    let open = 0, timer = 0, held = false, seen = false;
    const film = (card) => card.querySelector('video');
    const play = (card) => {
      const video = film(card);
      if (!video) return;
      if (!video.getAttribute('src')) { video.src = video.dataset.src; video.load(); }
      const go = video.play();
      const show = () => card.classList.add('is-playing');
      if (go && go.then) go.then(show, () => {}); else show();
    };
    const stop = (card) => {
      const video = film(card);
      card.classList.remove('is-playing');
      if (video && !video.paused) video.pause();
    };
    const fill = (card) => {
      const bar = card.querySelector('.oh-series__time i');
      if (!bar) return;
      bar.getAnimations().forEach((animation) => animation.cancel());
      if (!held && seen && wide.matches) bar.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], { duration: CYCLE_MS, easing: 'linear', fill: 'forwards' });
    };
    const next = () => {
      clearTimeout(timer);
      if (held || !seen || !wide.matches) return;
      timer = setTimeout(() => show((open + 1) % cards.length), CYCLE_MS);
    };
    const show = (index) => {
      open = index;
      cards.forEach((card, n) => {
        card.classList.toggle('is-open', n === index);
        if (!wide.matches) return;
        if (n === index && seen) play(card); else stop(card);
      });
      fill(cards[index]);
      next();
    };
    cards.forEach((card, index) => {
      card.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse' && wide.matches && open !== index) show(index); });
      card.addEventListener('focus', () => { if (open !== index) show(index); });
    });
    const track = root.querySelector('.oh-series__track') || root;
    track.addEventListener('pointerenter', (event) => { if (event.pointerType !== 'mouse') return; held = true; clearTimeout(timer); fill(cards[open]); });
    track.addEventListener('pointerleave', (event) => { if (event.pointerType !== 'mouse') return; held = false; fill(cards[open]); next(); });
    watch((entries) => {
      seen = entries[entries.length - 1].isIntersecting;
      if (wide.matches) show(open); else if (!seen) cards.forEach(stop);
    }, { threshold: 0.2 }).observe(track);
    // Narrow screens: the film of the card that is well in view plays.
    const inView = watch((entries) => entries.forEach((entry) => {
      if (wide.matches) return;
      if (entry.intersectionRatio >= 0.6) play(entry.target); else stop(entry.target);
    }), { threshold: [0, 0.6, 1] });
    cards.forEach((card) => inView.observe(card));
    wide.addEventListener('change', () => { cards.forEach(stop); show(open); });
    show(0);
  }

  // ------------------------------------------------------------------------------------------------ The numbers
  function counter(el) {
    if (el.dataset.ready) return;
    el.dataset.ready = 'true';
    const to = Number(el.dataset.ohCount);
    if (!Number.isFinite(to)) return;
    const value = el.querySelector('[data-oh-value]') || el;
    const grouped = el.dataset.grouped === 'true';
    const lang = document.documentElement.lang || 'el';
    const format = (n) => (grouped ? Math.round(n).toLocaleString(lang.startsWith('en') ? 'en-US' : 'el-GR') : String(Math.round(n)));
    value.textContent = format(0);
    const seen = watch((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      seen.disconnect();
      const start = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - start) / 1700);
        value.textContent = format(to * (1 - Math.pow(1 - t, 4)));
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, { threshold: 0.6 });
    seen.observe(el);
  }

  // ------------------------------------------------------------------------------------------------ Sleep Match
  // The guide of /pages/find-your-mattress (sections/page-find-mattress.liquid) as it is: its five questions, its
  // mattresses (the same list, sections/orion-sleep-match-data.liquid, fetched once when the quiz is first touched), its
  // ranking and its results, line for line. Only the look and the motion are the homepage's: the card grows or shrinks
  // smoothly from one question to the next, the answers rise in one after the other, and so do the results.
  const MATCH_EASE = 'cubic-bezier(.22,.7,.2,1)';
  const PHONE = window.matchMedia('(max-width: 749px)');
  const MATCH_ARROW = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';
  const MATCH_BACK = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 12H5M11 6l-6 6 6 6"/></svg>';
  function sleepMatch(finder) {
    if (finder.dataset.ready) return;
    finder.dataset.ready = 'true';
    let words;
    try { words = JSON.parse(finder.querySelector('[data-words]').textContent); } catch (error) { return; }
    const fill = (text, values) => String(text).replace(/\{(\w+)\}/g, (all, key) => (key in values ? values[key] : all));
    const english = finder.dataset.lang === 'en';
    const steps = Array.from(finder.querySelectorAll('[data-step]'));
    const progress = finder.querySelector('[data-progress]');
    const progressLabel = finder.querySelector('[data-progress-label]');
    const progressPercent = finder.querySelector('[data-progress-percent]');
    const progressBar = finder.querySelector('[data-progress-bar]');
    const actions = finder.querySelector('[data-actions]');
    const backButton = finder.querySelector('[data-back]');
    const nextButton = finder.querySelector('[data-next]');
    const nextLabel = nextButton.querySelector('span');
    const results = finder.querySelector('[data-results]');
    const heightInput = finder.querySelector('[data-height]');
    const weightInput = finder.querySelector('[data-weight]');
    const bodyError = finder.querySelector('[data-body-error]');
    const failed = finder.querySelector('[data-failed]');
    const answers = { age: 'adult', position: '', feel: '', base: 'solid', priority: '' };
    let currentStep = 0;
    let busy = false;

    // The mattresses: the page's own list, fetched once.
    let products = null;
    let loading = null;
    const load = () => {
      if (products) return Promise.resolve(products);
      if (!loading) {
        loading = fetch(finder.dataset.source, { credentials: 'same-origin' })
          .then((response) => { if (!response.ok) throw new Error(String(response.status)); return response.text(); })
          .then((html) => {
            const found = html.match(/<script[^>]*data-finder-products[^>]*>([\s\S]*?)<\/script>/);
            try { products = JSON.parse(found ? found[1] : '[]'); } catch (error) { products = []; }
            return products;
          })
          .catch((error) => { loading = null; throw error; });
      }
      return loading;
    };
    ['pointerdown', 'focusin', 'keydown'].forEach((type) => finder.addEventListener(type, () => { load().catch(() => {}); }, { once: true }));

    const hardnessCategories = [
      { key: 'soft', min: 1, max: 3.5, filterValues: ['1.0','1.5','2.0','2.5','3.0','3.5'] },
      // 4.5 is «Μέτριο» on every mattress page, so it belongs to «Μέτριο» here too (owner, 2026-10-01: item 63).
      { key: 'medium-soft', min: 4, max: 4, filterValues: ['4.0'] },
      { key: 'medium', min: 4.5, max: 5, filterValues: ['4.5','5.0'] },
      { key: 'medium-hard', min: 5.5, max: 6, filterValues: ['5.5','6.0'] },
      { key: 'hard', min: 6.5, max: Infinity, filterValues: ['6.5','7.0','7.5','8.0','8.5'] }
    ].map((category, index) => ({ ...category, label: words.categories[index][0], display: words.categories[index][1] }));

    const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[character]);
    const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
    const normalize = (value) => String(value || '').toLocaleLowerCase('el-GR');
    const materialPattern = /latex|λάτεξ|λατεξ|memory|visco|μνήμ|coco|κοκο|tencel|cotton|βαμβ|wool|μαλλ|afrolatex/;
    const priorityPatterns = {
      back: /ορθοπεδ|orthopedic/,
      motion: /pocket|tablet/,
      cool: /cool\s*max|coolmax/,
      natural: materialPattern
    };
    const priorityLabels = words.priorityLabels;
    const emptyPriorityCopy = words.empty;

    function productHaystack(product) {
      return normalize(`${product.title} ${(product.tags || []).join(' ')} ${product.description || ''}`);
    }

    function categoryContains(category, hardness) {
      return hardness >= category.min && (!Number.isFinite(category.max) || hardness <= category.max);
    }

    function matchesPriority(product) {
      const pattern = priorityPatterns[answers.priority];
      return !pattern || pattern.test(productHaystack(product));
    }

    // The page brings the quiz back into view at every step; here only when its top has gone above the screen (the
    // header stays on top on narrow screens). In focus (a phone) it keeps the card in the middle of the screen instead.
    function scrollToQuiz(height) {
      if (focused) { centre(height); return; }
      const header = document.querySelector('.orion-header__bar');
      const holder = header && header.closest('.shopify-section');
      const position = holder ? getComputedStyle(holder).position : '';
      const offset = position === 'sticky' || position === 'fixed' ? holder.getBoundingClientRect().height : 0;
      const top = finder.getBoundingClientRect().top - offset - 16;
      if (top < 0) window.scrollTo({ top: window.scrollY + top, behavior: 'smooth' });
    }

    // ---------------------------------------------------------------------------------- Focus on phones
    // Owner, 2026-10-01: «γιατί να μην κάνει απλά focus και να μην φαίνονται πάνω κάτω τα άλλα, και να μένει στο κέντρο
    // αν πατάς και πας στο επόμενο». A touch on the guide (an answer, "Συνέχεια", anywhere on it) brings the card to the
    // middle of the screen and lets the rest of the page, header included, fade away, so only the guide is seen; it stays
    // in the middle from question to question. A scroll of one's own, a touch outside it or Escape brings the page back;
    // a touch on the guide focuses it again. The card stays in its place in the page, with its own words and logic.
    const section = finder.closest('.shopify-section') || finder;
    let focused = false;
    let focusAbort = null;
    // The card in the middle of the screen (its top 12 px down when it is taller than the screen, as the results are).
    function centre(height) {
      const box = finder.getBoundingClientRect();
      const tall = height || box.height;
      const top = tall < window.innerHeight - 24 ? (window.innerHeight - tall) / 2 : 12;
      window.scrollTo({ top: window.scrollY + box.top - top, behavior: 'smooth' });
    }
    function focusGuide() {
      if (focused || !PHONE.matches) return;
      focused = true;
      document.documentElement.classList.add('oh-focus');
      section.classList.add('oh-focus__on');
      centre();
      focusAbort = new AbortController();
      const signal = focusAbort.signal;
      const leave = () => unfocusGuide();
      // A scroll of one's own: the wheel, an up or down swipe, the keys that scroll. The page's own move to the middle
      // is not one of them.
      window.addEventListener('wheel', (event) => { if (Math.abs(event.deltaY) > 2 && Math.abs(event.deltaY) >= Math.abs(event.deltaX)) leave(); }, { passive: true, signal });
      let start = null;
      window.addEventListener('touchstart', (event) => { const touch = event.touches[0]; start = touch ? { x: touch.clientX, y: touch.clientY } : null; }, { passive: true, signal });
      window.addEventListener('touchmove', (event) => {
        const touch = event.touches[0];
        if (!start || !touch) return;
        const dx = touch.clientX - start.x;
        const dy = touch.clientY - start.y;
        if (Math.abs(dy) > 12 && Math.abs(dy) > Math.abs(dx)) { start = null; leave(); }
      }, { passive: true, signal });
      window.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') { leave(); return; }
        if (event.target.closest && event.target.closest('input')) return;
        if (['PageDown', 'PageUp', 'ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key) || (event.key === ' ' && !event.target.closest('button'))) leave();
      }, { signal });
      // A touch outside the guide (the faded page takes no touches, so nothing there is pressed by mistake).
      document.addEventListener('pointerdown', (event) => { if (!finder.contains(event.target)) leave(); }, { capture: true, signal });
    }
    function unfocusGuide() {
      if (!focused) return;
      focused = false;
      focusAbort?.abort();
      document.documentElement.classList.remove('oh-focus');
      section.classList.remove('oh-focus__on');
    }
    finder.addEventListener('click', () => { if (!focused) focusGuide(); }, true);
    PHONE.addEventListener('change', () => { if (!PHONE.matches) unfocusGuide(); });

    function updateStep() {
      steps.forEach((step, index) => {
        const active = index === currentStep;
        step.hidden = !active;
        step.classList.toggle('is-active', active);
      });
      const stepNumber = currentStep + 1;
      const percent = stepNumber * 20;
      progressLabel.textContent = `${words.question} ${String(stepNumber).padStart(2, '0')} / 05`;
      progressPercent.textContent = `${percent}%`;
      progressBar.style.width = `${percent}%`;
      backButton.disabled = currentStep === 0;
      nextLabel.textContent = currentStep === 4 ? words.results : (currentStep === 0 && answers.age && answers.age !== 'adult' ? words.options : words.next);
      updateNextState();
    }

    function updateNextState() {
      const required = ['age', 'position', 'feel', 'base', 'priority'];
      if (currentStep === 3) {
        nextButton.disabled = !validBody(false);
        return;
      }
      nextButton.disabled = !answers[required[currentStep]];
    }

    function validBody(showError = true) {
      const height = Number(heightInput.value);
      const weight = Number(weightInput.value);
      const valid = height >= 140 && height <= 220 && weight >= 40 && weight <= 220;
      if (showError) bodyError.hidden = valid;
      return valid;
    }

    function seriesFromProduct(product) {
      const haystack = productHaystack(product);
      if (haystack.includes('armonia')) return 'ARMONIA';
      if (haystack.includes('smart')) return 'SMART';
      if (haystack.includes('comfort')) return 'COMFORT';
      if (haystack.includes('filoxenia')) return 'FILOXENIA';
      return 'ORION STROM';
    }

    function calculateProfile() {
      const baseIndex = Math.max(0, hardnessCategories.findIndex(category => category.key === answers.feel));
      const height = Number(heightInput.value);
      const weight = Number(weightInput.value);
      const referenceWeight = height - 100;
      const excessWeight = Math.max(0, weight - referenceWeight);
      const shiftCount = Math.floor(excessWeight / 20);
      const adjustedIndex = clamp(baseIndex + shiftCount, 0, hardnessCategories.length - 1);
      const category = hardnessCategories[adjustedIndex];
      const target = Number.isFinite(category.max) ? (category.min + category.max) / 2 : category.min;
      return { baseCategory: hardnessCategories[baseIndex], category, referenceWeight, excessWeight, shiftCount, target };
    }

    function productScore(product, profile) {
      const haystack = productHaystack(product);
      const hardness = Number(product.hardness);
      const exactRange = categoryContains(profile.category, hardness);
      let score = 82 - Math.abs(hardness - profile.target) * 9 + (exactRange ? 14 : 0);
      const reasons = [];
      const isPocket = /pocket|tablet/.test(haystack);
      const isBonnell = /bonnell|ορθοπεδ/.test(haystack);
      if (isPocket) score += 2;

      if (exactRange) reasons.push(words.reasons.exact);
      else reasons.push(words.reasons.nearest);

      if (answers.position === 'side') {
        if (isPocket || /latex|soft|pillow/.test(haystack)) score += 6;
        reasons.push(words.reasons.side);
      }
      if (answers.position === 'back') {
        if (hardness >= 4.5 && hardness <= 6.5) score += 5;
        reasons.push(words.reasons.back);
      }
      if (answers.position === 'stomach') {
        if (hardness >= 5 && hardness <= 7) score += 6;
        reasons.push(words.reasons.stomach);
      }
      if (answers.position === 'mixed') {
        if (isPocket) score += 5;
        reasons.push(words.reasons.mixed);
      }

      if (answers.priority === 'back') {
        score += 16;
        reasons.unshift(words.reasons.pBack);
      }
      if (answers.priority === 'motion') {
        score += 16;
        reasons.unshift(words.reasons.pMotion);
      }
      if (answers.priority === 'cool') {
        score += 18;
        reasons.unshift(words.reasons.pCool);
      }
      if (answers.priority === 'natural') {
        score += 18;
        reasons.unshift(words.reasons.pNatural);
      }

      if (answers.base === 'flex' && (isPocket || /latex|memory|visco/.test(haystack))) score += 4;
      if ((answers.base === 'solid' || answers.base === 'rigid') && isBonnell) score += 2;

      // ARMONIA first (owner, 2026-10-01: the newest series, the closest to the company's new character): enough to put
      // its mattresses that fit ahead of every other series.
      const series = seriesFromProduct(product);
      if (series === 'ARMONIA') score += 24;

      return { ...product, hardness, rankScore: score + tieShare(product), score: clamp(Math.round(score), 62, 98), reasons: Array.from(new Set(reasons)).slice(0, 2), series };
    }

    // Many mattresses share a score, and the collection's order used to settle every tie, so only about 70 of the 250
    // were ever suggested (owner, 2026-10-01: item 63). A small fixed share by answers and mattress, under one point,
    // now settles ties: the same answers always give the same mattresses, and different answers reach more of them.
    function tieShare(product) {
      const key = `${answers.position}|${answers.feel}|${answers.priority}|${answers.base}|${product.url}`;
      let hash = 2166136261;
      for (let i = 0; i < key.length; i++) { hash ^= key.charCodeAt(i); hash = Math.imul(hash, 16777619); }
      return ((hash >>> 0) % 1000) / 1000 * 0.9;
    }

    function filteredProducts() {
      const excluded = /ανωστρω|ανώστρω|topper|baby|βρεφ|infant|παιδικ|αντιγραφ/;
      return products.filter(product => {
        const hardness = Number(product.hardness);
        // Never a roll-pack mattress, ATLAS or any to come (owner, 2026-10-01).
        const rollPack = (product.tags || []).includes('roll-pack') || /atlas/i.test(product.title);
        return !rollPack && product.available !== false && product.hardness !== null && Number.isFinite(hardness) && hardness >= 0 && hardness <= 10 && !excluded.test(normalize(product.title));
      });
    }

    function topMatches(profile) {
      const candidates = filteredProducts().filter(matchesPriority);
      const scored = candidates.map(product => productScore(product, profile));
      let ranked = scored.filter(product => categoryContains(profile.category, product.hardness)).sort((a, b) => b.rankScore - a.rankScore);
      // Nothing in the range with that priority (e.g. «Μαλακό» with «Στήριξη μέσης»): the closest firmness that has it,
      // marked as the nearest choice, instead of an empty result (owner, 2026-10-01: item 63).
      if (!ranked.length) ranked = scored.sort((a, b) => (Math.abs(a.hardness - profile.target) - Math.abs(b.hardness - profile.target)) || (b.rankScore - a.rankScore));
      // Up to three ARMONIA mattresses first; then the other series one each, so ARMONIA always has the most (two
      // each, as before, when no ARMONIA fits).
      const armonia = ranked.filter(product => product.series === 'ARMONIA');
      const selected = armonia.slice(0, 3);
      const seriesCounts = {};
      const perSeries = armonia.length ? 1 : 2;
      ranked.forEach(product => {
        if (selected.length >= 4 || product.series === 'ARMONIA') return;
        if ((seriesCounts[product.series] || 0) >= perSeries) return;
        selected.push(product);
        seriesCounts[product.series] = (seriesCounts[product.series] || 0) + 1;
      });
      if (selected.length < 4) ranked.forEach(product => { if (selected.length < 4 && !selected.includes(product)) selected.push(product); });
      return selected;
    }

    function categoryFilterValues(category) {
      return category.filterValues;
    }

    // The firmness as the mattress pages draw it: ten steps, lit up to the score (a half step for .5).
    const tenSteps = (value) => Array.from({ length: 10 }, (item, index) => {
      const step = index + 1;
      const lit = step <= value ? ' class="is-on"' : (step - 0.5 === value ? ' class="is-on is-half"' : '');
      return `<i style="--s:${index}"${lit}></i>`;
    }).join('');

    function resultCard(product, index) {
      const image = product.image ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="${index === 0 ? 'eager' : 'lazy'}" width="900" height="760">` : '';
      const rank = index === 0 ? words.best : fill(words.alternative, { n: String(index).padStart(2, '0') });
      const firmness = `${escapeHtml(product.firmnessLabel)} · ${product.hardness.toLocaleString(english ? 'en-US' : 'el-GR', { maximumFractionDigits: 1 })}/10`;
      return `
        <li class="oh-match__item" data-in style="--c:${index}">
          <article class="oh-match__product${index === 0 ? ' is-best' : ''}">
            <div class="oh-match__media">
              ${image}
              <span class="oh-match__score" data-score="${product.score}">${escapeHtml(fill(words.match, { n: product.score }))}</span>
            </div>
            <div class="oh-match__info">
              <p class="oh-match__rank">${escapeHtml(rank)} · ${escapeHtml(product.series)}</p>
              <h4 class="oh-match__name"><a href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a></h4>
              <p class="oh-match__feel"><span>${escapeHtml(words.firmness)}</span><span>${firmness}</span></p>
              <span class="oh-steps" aria-hidden="true">${tenSteps(product.hardness)}</span>
              <div class="oh-match__foot">
                <span class="oh-match__price">${escapeHtml(product.price)}</span>
                <i class="oh-match__go" aria-hidden="true">${MATCH_ARROW}</i>
              </div>
            </div>
          </article>
        </li>`;
    }

    const advice = () => `<p class="oh-match__advice">${words.advice} <a href="${escapeHtml(finder.dataset.stores)}">${escapeHtml(words.stores)}${MATCH_ARROW}</a></p>`;

    // Moving between questions: what is shown fades out, the card's height follows the new content smoothly (the
    // page below with it), and the new parts rise in one after the other from the side the quiz is heading to.
    function go(direction, change, after) {
      if (busy) return;
      busy = true;
      const shown = () => finder.querySelector('[data-step]:not([hidden]), [data-results]:not([hidden])');
      const leaving = shown();
      const fade = leaving ? leaving.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: `translate3d(${-direction * 22}px,0,0)` }], { duration: 190, easing: 'ease-in', fill: 'forwards' }) : null;
      const swap = () => {
        const from = finder.getBoundingClientRect().height;
        change();
        if (fade) fade.cancel();
        const to = finder.getBoundingClientRect().height;
        if (Math.abs(to - from) > 1) finder.animate([{ height: `${from}px` }, { height: `${to}px` }], { duration: 680, easing: MATCH_EASE });
        const entering = shown();
        if (entering) {
          Array.from(entering.querySelectorAll('[data-in]')).forEach((part, index) => part.animate(
            [{ opacity: 0, transform: `translate3d(${direction * 26}px,12px,0)` }, { opacity: 1, transform: 'none' }],
            { duration: 760, delay: 60 + Math.min(index, 10) * 55, easing: MATCH_EASE, fill: 'backwards' }
          ));
          const heading = entering.querySelector('[tabindex="-1"]');
          if (heading && finder.contains(document.activeElement)) heading.focus({ preventScroll: true });
        }
        scrollToQuiz(to);
        busy = false;
        if (after) after(entering);
      };
      if (fade) fade.finished.then(swap, swap); else swap();
    }

    // The match counts up as each suggestion rises in.
    function countUp(scope) {
      scope.querySelectorAll('[data-score]').forEach((badge, index) => {
        const to = Number(badge.dataset.score);
        const start = performance.now() + 260 + index * 110;
        const tick = (now) => {
          const t = clamp((now - start) / 1100, 0, 1);
          badge.textContent = fill(words.match, { n: Math.round(to * (1 - Math.pow(1 - t, 4))) });
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      });
      scope.querySelectorAll('.oh-match__media img').forEach((image) => {
        const media = image.closest('.oh-match__media');
        const done = () => media.classList.add('is-loaded');
        if (image.complete) done(); else { image.addEventListener('load', done, { once: true }); image.addEventListener('error', done, { once: true }); }
      });
    }

    function waiting(on) {
      actions.classList.toggle('is-waiting', on);
      nextButton.classList.toggle('is-busy', on);
      nextButton.setAttribute('aria-busy', on ? 'true' : 'false');
      nextLabel.textContent = on ? words.wait : words.results;
    }

    function showAdultResults() {
      if (!products) {
        if (nextButton.classList.contains('is-busy')) return;
        failed.hidden = true;
        waiting(true);
        load().then(() => { waiting(false); showAdultResults(); }, () => { waiting(false); failed.textContent = words.failed; failed.hidden = false; });
        return;
      }
      const profile = calculateProfile();
      const matches = topMatches(profile);
      const hardnessFilter = categoryFilterValues(profile.category).join(',');
      // When nothing fits the range itself, the cards are the closest firmness, and the heading says so (item 63).
      const closest = matches.length > 0 && !categoryContains(profile.category, matches[0].hardness);
      const heading = closest ? words.closest : (matches.length === 1 ? words.one : (matches.length > 1 ? fill(words.many, { n: matches.length }) : words.none));
      go(1, () => {
        steps.forEach(step => { step.hidden = true; });
        progress.hidden = true;
        actions.hidden = true;
        failed.hidden = true;
        results.hidden = false;
        results.innerHTML = `
          <div class="oh-match__result-head" data-in>
            <div>
              <h3 class="oh-match__result-title" tabindex="-1">${escapeHtml(heading)}</h3>
            </div>
            <div class="oh-match__target">
              <small>${escapeHtml(words.range)}</small>
              <strong>${escapeHtml(profile.category.label)} · ${escapeHtml(profile.category.display)}</strong>
            </div>
          </div>
          ${matches.length ? `<ul class="oh-match__list">${matches.map(resultCard).join('')}</ul>` : `<div class="oh-match__branch" data-in><p>${escapeHtml(emptyPriorityCopy[answers.priority])}</p></div>`}
          <div class="oh-match__after" data-in>
            <button type="button" class="oh-match__restart" data-restart>${MATCH_BACK}<span>${escapeHtml(words.restart)}</span></button>
            <a class="oh-pill oh-pill--line" href="${escapeHtml(finder.dataset.mattresses)}?filter.p.m.custom.hardness=${hardnessFilter}"><span>${escapeHtml(words.all)}</span>${MATCH_ARROW}</a>
            ${advice()}
          </div>`;
        finder.querySelector('[data-restart]').addEventListener('click', restart);
      }, countUp);
    }

    function showAgeResult() {
      const infant = answers.age === 'infant';
      const url = infant ? finder.dataset.infants : `${finder.dataset.mattresses}?filter.p.m.custom.hardness=6.0,6.5,7.0,7.5,8.0,8.5`;
      const icon = infant ? '0–2' : '3–16';
      const title = infant ? words.infantTitle : words.childTitle;
      const copy = infant ? words.infantText : words.childText;
      go(1, () => {
        steps.forEach(step => { step.hidden = true; });
        progress.hidden = true;
        actions.hidden = true;
        failed.hidden = true;
        results.hidden = false;
        results.innerHTML = `
          <div class="oh-match__branch">
            <span class="oh-match__badge" data-in>${icon}</span>
            <h3 class="oh-match__result-title" tabindex="-1" data-in>${escapeHtml(title)}</h3>
            ${copy ? `<p data-in>${escapeHtml(copy)}</p>` : ''}
            <a class="oh-pill" href="${escapeHtml(url)}" data-in><span>${escapeHtml(words.suitable)}</span>${MATCH_ARROW}</a>
          </div>
          <div class="oh-match__after" data-in>
            <button type="button" class="oh-match__restart" data-restart>${MATCH_BACK}<span>${escapeHtml(words.restartOne)}</span></button>
            ${advice()}
          </div>`;
        finder.querySelector('[data-restart]').addEventListener('click', restart);
      });
    }

    function restart() {
      go(-1, () => {
        Object.assign(answers, { age: 'adult', position: '', feel: '', base: 'solid', priority: '' });
        currentStep = 0;
        finder.querySelectorAll('[data-choice-key]').forEach(button => {
          const selected = (button.dataset.choiceKey === 'age' && button.dataset.choiceValue === 'adult') || (button.dataset.choiceKey === 'base' && button.dataset.choiceValue === 'solid');
          button.classList.toggle('is-selected', selected);
          button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
        heightInput.value = 175;
        weightInput.value = 75;
        bodyError.hidden = true;
        results.hidden = true;
        results.innerHTML = '';
        progress.hidden = false;
        actions.hidden = false;
        updateStep();
      });
    }

    finder.addEventListener('click', event => {
      const choice = event.target.closest('[data-choice-key]');
      if (!choice || !finder.contains(choice)) return;
      const key = choice.dataset.choiceKey;
      const value = choice.dataset.choiceValue;
      answers[key] = value;
      finder.querySelectorAll(`[data-choice-key="${key}"]`).forEach(button => {
        const selected = button === choice;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      updateNextState();
      if (currentStep === 0) nextLabel.textContent = value === 'adult' ? words.next : words.options;
    });

    heightInput.addEventListener('input', updateNextState);
    weightInput.addEventListener('input', updateNextState);
    [heightInput, weightInput].forEach((input) => input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && !nextButton.disabled) { event.preventDefault(); nextButton.click(); }
    }));

    // The − and + beside each number: one step a press, and on and on while held.
    finder.querySelectorAll('[data-nudge]').forEach((button) => {
      const input = button.closest('.oh-match__dial').querySelector('input');
      let wait = 0, repeat = 0;
      const nudge = () => {
        const current = input.value === '' ? Number(input.defaultValue) : Math.round(Number(input.value));
        const next = clamp((Number.isFinite(current) ? current : Number(input.defaultValue)) + Number(button.dataset.nudge), Number(input.min), Number(input.max));
        if (String(next) === input.value) return;
        input.value = next;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.animate([{ transform: `translateY(${Number(button.dataset.nudge) > 0 ? -3 : 3}px)`, opacity: 0.55 }, { transform: 'none', opacity: 1 }], { duration: 220, easing: MATCH_EASE });
      };
      const stop = () => { clearTimeout(wait); clearInterval(repeat); };
      button.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        nudge();
        stop();
        wait = setTimeout(() => { repeat = setInterval(nudge, 75); }, 420);
      });
      ['pointerup', 'pointerleave', 'pointercancel', 'blur'].forEach((type) => button.addEventListener(type, stop));
      button.addEventListener('click', (event) => { if (event.detail === 0) nudge(); });
    });

    nextButton.addEventListener('click', () => {
      if (busy) return;
      if (currentStep === 0 && answers.age !== 'adult') { showAgeResult(); return; }
      if (currentStep === 3 && !validBody(true)) return;
      if (currentStep === 4) { showAdultResults(); return; }
      go(1, () => { currentStep += 1; updateStep(); });
    });

    backButton.addEventListener('click', () => {
      if (currentStep === 0 || busy) return;
      go(-1, () => { currentStep -= 1; updateStep(); });
    });

    updateStep();
  }

  // ------------------------------------------------------------------------------------------------ The voices
  function voices(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const track = root.querySelector('[data-oh-track]');
    if (!track) return;
    const step = (direction) => {
      const first = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 16;
      const width = first ? first.getBoundingClientRect().width + gap : track.clientWidth;
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      const atStart = track.scrollLeft <= 4;
      let left = track.scrollLeft + direction * width;
      if (direction > 0 && atEnd) left = 0;
      if (direction < 0 && atStart) left = track.scrollWidth;
      track.scrollTo({ left, behavior: 'smooth' });
    };
    root.querySelector('[data-oh-prev]')?.addEventListener('click', () => { step(-1); hold(); });
    root.querySelector('[data-oh-next]')?.addEventListener('click', () => { step(1); hold(); });
    let timer = 0, over = false, seen = false, touched = 0;
    const loop = () => {
      clearInterval(timer);
      if (!over && seen && Date.now() > touched) timer = setInterval(() => step(1), 7000);
    };
    const hold = () => { touched = Date.now() + 9000; loop(); setTimeout(loop, 9100); };
    root.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') { over = true; loop(); } });
    root.addEventListener('pointerleave', (event) => { if (event.pointerType === 'mouse') { over = false; loop(); } });
    track.addEventListener('pointerdown', hold, { passive: true });
    track.addEventListener('wheel', hold, { passive: true });
    watch((entries) => { seen = entries[entries.length - 1].isIntersecting; loop(); }, { threshold: 0.3 }).observe(root);
  }

  // The parts start one at a time, so the first load never runs them all in one go (owner, 2026-10-01: a fast homepage
  // on phones): the series at once (they follow the first banner), the numbers and the voices a moment later, the
  // Sleep Match when it nears the screen — the same guide, only started later.
  const soon = (task) => setTimeout(task, 0);
  const near = watch((entries) => entries.forEach((entry) => {
    if (!entry.isIntersecting) return;
    near.unobserve(entry.target);
    if (entry.target.ohStart) entry.target.ohStart();
  }), { rootMargin: '150% 0px' });
  const whenNear = (el, start) => { el.ohStart = start; near.observe(el); };

  const init = (scope) => {
    scope.querySelectorAll('.oh').forEach((block) => rest.observe(block));
    scope.querySelectorAll('[data-oh-reveal]').forEach((el) => reveal.observe(el));
    scope.querySelectorAll('[data-oh-series]').forEach(series);
    soon(() => scope.querySelectorAll('[data-oh-count]').forEach(counter));
    scope.querySelectorAll('[data-oh-match]').forEach((finder) => whenNear(finder, () => sleepMatch(finder)));
    soon(() => scope.querySelectorAll('[data-oh-voices]').forEach(voices));
  };
  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
